import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, UserPlus, Phone, ListChecks, Building2, Blocks } from "lucide-react";
import { apiGet } from "@/lib/api";
import { useSEO } from "@/lib/seo";

const STATUS_COLORS = {
  New: "bg-blue-100 text-blue-700", Contacted: "bg-amber-100 text-amber-700",
  Qualified: "bg-purple-100 text-purple-700", "Site Visit": "bg-cyan-100 text-cyan-700",
  Converted: "bg-green-100 text-green-700", Lost: "bg-red-100 text-red-700",
};

function StatCard({ icon: Icon, label, value, to }) {
  const inner = (
    <div className="p-6 rounded-lg border border-border bg-white hover:shadow-md transition-shadow">
      <div className="flex items-center justify-between">
        <div className="h-10 w-10 rounded-sm bg-primary/5 text-primary grid place-items-center"><Icon className="h-5 w-5" aria-hidden /></div>
        <span className="font-display text-3xl text-foreground">{value ?? "—"}</span>
      </div>
      <p className="text-sm text-muted-foreground mt-3">{label}</p>
    </div>
  );
  return to ? <Link to={to}>{inner}</Link> : inner;
}

export default function Dashboard() {
  const [s, setS] = useState(null);
  useSEO({ title: "Admin Dashboard" });
  useEffect(() => { apiGet("/admin/stats").then(setS).catch(() => {}); }, []);

  return (
    <div>
      <h1 className="font-display text-3xl text-foreground mb-1">Dashboard</h1>
      <p className="text-muted-foreground mb-8">Overview of leads and inventory.</p>

      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        <StatCard icon={Users} label="Total Leads" value={s?.total_leads} to="/admin/leads" />
        <StatCard icon={UserPlus} label="New Leads" value={s?.new_leads} to="/admin/leads?status=New" />
        <StatCard icon={Phone} label="Contact Requests" value={s?.contact_requests} />
        <StatCard icon={ListChecks} label="Requirements" value={s?.requirement_requests} />
        <StatCard icon={Building2} label="Properties" value={s?.properties} to="/admin/properties" />
        <StatCard icon={Blocks} label="Projects" value={s?.projects} to="/admin/projects" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mt-8">
        <div className="lg:col-span-2 rounded-lg border border-border bg-white overflow-hidden">
          <div className="p-5 border-b border-border flex items-center justify-between">
            <h2 className="font-display text-xl text-foreground">Recent Leads</h2>
            <Link to="/admin/leads" className="text-sm font-semibold text-primary">View all</Link>
          </div>
          <div className="divide-y divide-border">
            {s?.recent_leads?.length ? s.recent_leads.map((l) => (
              <Link to={`/admin/leads?open=${l.id}`} key={l.id} className="flex items-center justify-between p-4 hover:bg-muted/40 transition-colors">
                <div>
                  <p className="font-medium text-foreground">{l.name}</p>
                  <p className="text-xs text-muted-foreground">{l.phone} · {l.source}{l.location ? ` · ${l.location}` : ""}</p>
                </div>
                <span className={`text-[11px] font-semibold px-2 py-1 rounded-sm ${STATUS_COLORS[l.status] || "bg-muted"}`}>{l.status}</span>
              </Link>
            )) : <p className="p-6 text-sm text-muted-foreground">No leads yet.</p>}
          </div>
        </div>

        <div className="space-y-6">
          <div className="rounded-lg border border-border bg-white p-5">
            <h2 className="font-display text-lg text-foreground mb-3">Leads by Status</h2>
            <div className="space-y-2">
              {s?.by_status && Object.entries(s.by_status).map(([k, v]) => (
                <div key={k} className="flex items-center justify-between text-sm">
                  <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-sm ${STATUS_COLORS[k]}`}>{k}</span>
                  <span className="font-medium text-foreground">{v}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-lg border border-border bg-white p-5">
            <h2 className="font-display text-lg text-foreground mb-3">Top Properties (Views)</h2>
            <div className="space-y-2">
              {s?.top_properties?.length ? s.top_properties.map((p) => (
                <div key={p.slug} className="flex items-center justify-between text-sm">
                  <span className="text-foreground truncate mr-2">{p.name}</span>
                  <span className="text-muted-foreground shrink-0">{p.views || 0}</span>
                </div>
              )) : <p className="text-sm text-muted-foreground">No data.</p>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
